import React from 'react';
import type { Asset } from '@/types/asset';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { cn } from '@/lib/utils';
import {
  Eye,
  Paperclip,
  MapPin,
  Calendar,
  DollarSign,
  Package,
  AlertTriangle,
} from 'lucide-react';
import { format } from 'date-fns';

interface AssetGalleryProps {
  assets: Asset[];
  viewMode: 'gallery' | 'list';
  gridSize?: 4 | 5 | 6;
  onAssetClick: (asset: Asset, mode?: 'edit' | 'view') => void;
  selectedAssetIds?: Set<string>;
  onSelectAsset?: (assetId: string, checked: boolean) => void;
}

export function AssetGallery({ assets, viewMode, gridSize = 4, onAssetClick, selectedAssetIds = new Set(), onSelectAsset }: AssetGalleryProps): React.ReactElement {
  const gridColsClass = {
    4: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4',
    5: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5',
    6: 'sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-6',
  }[gridSize];

  if (viewMode === 'gallery') {
    return (
      <div className={cn('grid gap-4', gridColsClass)}>
        {assets.map((asset) => (
          <AssetCard 
            key={asset.id} 
            asset={asset} 
            onClick={(mode) => onAssetClick(asset, mode)}
            isSelected={selectedAssetIds.has(asset.id)}
            onSelect={onSelectAsset ? (checked) => onSelectAsset(asset.id, checked) : undefined}
          />
        ))}
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-white dark:bg-black">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-[50px]"></TableHead>
            <TableHead className="w-[80px]">Image</TableHead>
            <TableHead>Asset ID</TableHead>
            <TableHead>Name</TableHead>
            <TableHead>Category</TableHead>
            <TableHead>Location</TableHead>
            <TableHead>Serial Number</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Acquisition Cost</TableHead>
            <TableHead className="text-right">Net Book Value</TableHead>
            <TableHead className="w-[80px]">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {assets.map((asset) => (
            <AssetTableRow
              key={asset.id}
              asset={asset}
              onClick={() => onAssetClick(asset)}
              isSelected={selectedAssetIds.has(asset.id)}
              onSelect={onSelectAsset ? (checked) => onSelectAsset(asset.id, checked) : undefined}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function AssetCard({ asset, onClick, isSelected, onSelect }: { asset: Asset; onClick: (mode?: 'edit' | 'view') => void; isSelected?: boolean; onSelect?: (checked: boolean) => void }) {
  const [imageError, setImageError] = React.useState(false);
  const summary = asset.alertSummary;

  const handleCardClick = () => {
    if (onSelect) {
      onSelect(!isSelected);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick('edit');
  };

  const handleAlertClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    // Open asset in view mode with alerts tab
    onClick('view');
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <Card 
      className={cn(
        "group cursor-pointer overflow-hidden transition-all duration-200 border border-gray-200 dark:border-gray-700",
        isSelected && "border-2 bg-green-50 dark:bg-green-950/20",
        !isSelected && "hover:shadow-lg"
      )}
      style={isSelected ? { borderColor: 'var(--primary)' } : undefined}
      onClick={handleCardClick}
    >
      <div className="relative aspect-video overflow-hidden bg-muted">
        {asset.imageUrl && !imageError ? (
          <img
            src={asset.imageUrl}
            alt={asset.name}
            className="h-full w-full object-cover transition-transform group-hover:scale-105"
            onError={handleImageError}
            referrerPolicy="no-referrer"
            crossOrigin="anonymous"
          />
        ) : (
          <div className="flex h-full items-center justify-center">
            <Package className="h-12 w-12 text-muted-foreground" />
          </div>
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 transition-opacity group-hover:opacity-100">
          <div className="absolute bottom-2 right-2 flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              className="h-7 w-7 p-0"
              onClick={handleEditClick}
              title="View/Edit Asset"
            >
              <Eye className="h-2.5 w-2.5" />
            </Button>
            {asset.attachments && asset.attachments.length > 0 && (
              <Button
                size="sm"
                variant="secondary"
                className="h-8 w-8 p-0"
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
              >
                <Paperclip className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        {asset.status.toLowerCase() !== 'active' && (
          <Badge
            className={cn(
              'absolute top-2 left-2',
              asset.status === 'maintenance' && 'bg-yellow-500',
              asset.status === 'inactive' && 'bg-gray-500',
              asset.status === 'disposed' && 'bg-red-500',
              asset.status === 'pending' && 'bg-blue-500'
            )}
          >
            {asset.status}
          </Badge>
        )}
      </div>
      <CardContent className="p-3">
        <div className="space-y-2">
          <div className="flex items-start justify-between gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold line-clamp-1">{asset.name}</h3>
              <p className="text-xs text-muted-foreground">{asset.assetId}</p>
            </div>
            {summary && summary.totalCount > 0 && (
              <Button
                size="sm"
                variant="outline"
                className={cn(
                  "h-7 px-2 gap-1 flex-shrink-0",
                  summary.criticalCount > 0 ? "border-red-500 text-red-600 hover:bg-red-50 dark:hover:bg-red-950" :
                  summary.warningCount > 0 ? "border-orange-500 text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-950" :
                  "border-blue-500 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-950"
                )}
                onClick={handleAlertClick}
                title={`${summary.totalCount} alert${summary.totalCount !== 1 ? 's' : ''}`}
              >
                <AlertTriangle className="h-3 w-3" />
                <span className="text-xs font-semibold">{summary.totalCount}</span>
              </Button>
            )}
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <div className="flex h-5 w-5 items-center justify-center rounded bg-[#F5B8B8]/30 dark:bg-[#F5B8B8]/10">
                <Package className="h-3 w-3 text-[#D88888] dark:text-[#F5B8B8]" />
              </div>
              <span className="text-xs text-muted-foreground truncate">{asset.category}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex h-5 w-5 items-center justify-center rounded bg-[#B4A7D6]/30 dark:bg-[#B4A7D6]/10">
                <MapPin className="h-3 w-3 text-[#8B7AB8] dark:text-[#B4A7D6]" />
              </div>
              <span className="text-xs text-muted-foreground truncate">{asset.location}</span>
            </div>
          </div>
          <div className="flex items-center justify-between border-t pt-2">
            <div>
              <p className="text-[10px] text-muted-foreground">NBV</p>
              <p className="text-xs font-semibold">
                {asset.currency} {asset.netBookValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
            </div>
            <div className="text-right">
              <p className="text-[10px] text-muted-foreground">Acquisition</p>
              <p className="text-xs">
                {asset.currency} {asset.acquisitionCost.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
              </p>
            </div>
          </div>
          {asset.serialNumber && (
            <p className="text-[10px] text-muted-foreground truncate">
              S/N: {asset.serialNumber}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function AssetTableRow({ asset, onClick, isSelected, onSelect }: { asset: Asset; onClick: () => void; isSelected?: boolean; onSelect?: (checked: boolean) => void }) {
  const [imageError, setImageError] = React.useState(false);

  const handleRowClick = () => {
    if (onSelect) {
      onSelect(!isSelected);
    }
  };

  const handleEditClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick();
  };

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const handleImageError = () => {
    setImageError(true);
  };

  return (
    <TableRow
      className={cn(
        "cursor-pointer",
        isSelected && "bg-green-50 dark:bg-green-950/20"
      )}
      data-state={isSelected ? "selected" : undefined}
      onClick={handleRowClick}
    >
      <TableCell onClick={handleCheckboxClick}>
        <Checkbox
          checked={isSelected}
          onCheckedChange={(checked) => onSelect && onSelect(!!checked)}
          aria-label={`Select ${asset.name}`}
        />
      </TableCell>
      <TableCell>
        <div className="h-12 w-12 overflow-hidden rounded bg-muted">
          {asset.imageUrl && !imageError ? (
            <img
              src={asset.imageUrl}
              alt={asset.name}
              className="h-full w-full object-cover"
              onError={handleImageError}
              referrerPolicy="no-referrer"
              crossOrigin="anonymous"
            />
          ) : (
            <div className="flex h-full items-center justify-center">
              <Package className="h-4 w-4 text-muted-foreground" />
            </div>
          )}
        </div>
      </TableCell>
      <TableCell className="font-medium">{asset.assetId}</TableCell>
      <TableCell>
        <div className="font-medium">{asset.name}</div>
      </TableCell>
      <TableCell>{asset.category}</TableCell>
      <TableCell>{asset.location}</TableCell>
      <TableCell className="text-muted-foreground">
        {asset.serialNumber || '-'}
      </TableCell>
      <TableCell>
        {asset.status.toLowerCase() !== 'active' ? (
          <Badge
            variant="outline"
            className={cn(
              asset.status === 'maintenance' && 'border-yellow-500 text-yellow-500',
              asset.status === 'inactive' && 'border-gray-500 text-gray-500',
              asset.status === 'disposed' && 'border-red-500 text-red-500',
              asset.status === 'pending' && 'border-blue-500 text-blue-500'
            )}
          >
            {asset.status}
          </Badge>
        ) : (
          <span className="text-muted-foreground">Active</span>
        )}
      </TableCell>
      <TableCell className="text-right">
        <div className="font-medium">
          {asset.currency} {asset.acquisitionCost.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
        </div>
      </TableCell>
      <TableCell className="text-right">
        <div className="font-medium">
          {asset.currency} {asset.netBookValue.toLocaleString('en-GB', { minimumFractionDigits: 2 })}
        </div>
      </TableCell>
      <TableCell>
        <Button
          variant="ghost"
          size="icon"
          onClick={handleEditClick}
          className="h-7 w-7"
          title="View/Edit Asset"
        >
          <Eye className="h-2.5 w-2.5" />
        </Button>
      </TableCell>
    </TableRow>
  );
}
